<script lang="ts">
	import CartQuantityStepper from "@components/common/CartQuantityStepper.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import giftIcon from "@core/assets/icons/gift.svg?raw";
	import plus from "@core/assets/icons/plus.svg?raw";
	import type { OrderPurchasedCoupon } from "@core/schema/OrderPurchasedCoupon.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import { maxCartAdditionAmount } from "../maxCartAdditionAmount.js";
	import translations from "./CartOverviewPurchasedCoupon.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	const { orderPurchasedCoupon }: { orderPurchasedCoupon: OrderPurchasedCoupon } = $props();
	const { tenant } = getGlobalTemplateProps();

	const purchasableCoupon = $derived(Cart.purchasableCouponsInOrder.find(({ id }) => id === orderPurchasedCoupon.id));

	function decrement(): void {
		if (!purchasableCoupon) {
			return;
		}
		Cart.setPurchasedCouponQuantity(purchasableCoupon, orderPurchasedCoupon.quantity - 1);
	}

	function removeFromCart(): void {
		if (!purchasableCoupon) {
			return;
		}
		Cart.setPurchasedCouponQuantity(purchasableCoupon, 0);
	}

	function increment(): void {
		if (!purchasableCoupon || orderPurchasedCoupon.quantity >= maxCartAdditionAmount) {
			return;
		}
		Cart.setPurchasedCouponQuantity(purchasableCoupon, orderPurchasedCoupon.quantity + 1);
	}
</script>

<div class="grid grid-cols-[6rem_auto] sm:grid-cols-[8rem_auto] xl:grid-cols-[10rem_auto] gap-y-2 gap-x-4 sm:gap-x-6">
	<div
		class="w-full aspect-square row-span-1 sm:row-span-2 bg-fcrdarkgray rounded-2xl flex items-center justify-center"
	>
		<Icon icon={giftIcon} class="size-10 text-white" />
	</div>
	<div class="sm:border-t border-fcrlightgray pt-2 2xl:pt-6 flex flex-col justify-center sm:justify-between">
		<div class="flex justify-between">
			<div class="text-fcrdarkgray">
				<div class="font-antipol font-medium text-base lg:text-lg 2xl:text-1.5xl/8">
					<Formatted
						text={translate("voucher", {
							amount: formatPrice(orderPurchasedCoupon.amount, orderPurchasedCoupon.currency, tenant.locale, false),
						})}
					/>
				</div>
				<div class="text-3 sm:text-3.5 xl:text-base">
					{#if selectedCurrency.current}
						<Formatted
							text={translate("perPiece", {
								price: formatPrice(orderPurchasedCoupon.amount, orderPurchasedCoupon.currency, tenant.locale, false),
							})}
						/>
					{/if}
				</div>
			</div>
			<button
				type="button"
				class={[
					"hidden sm:flex border-fcrgray-300 border size-12 items-center justify-center rounded-full",
					"[&:not(:disabled):hover]:border-fcrdarkgray ml-1",
					"[&:not(:disabled):hover:active]:scale-95 transition-transform duration-150 ease-in-out",
				]}
				onclick={removeFromCart}
				aria-label={translate("removeFromCart")}
			>
				<Icon icon={plus} class="size-5 2xl:size-6 rotate-45" />
			</button>
		</div>
	</div>
	<div
		class="w-full sm:border-b border-fcrlightgray sm:pb-2 2xl:pb-6 col-span-2 sm:col-span-1 flex items-end justify-end"
	>
		<CartQuantityStepper
			showZeroCount
			shrinkOnMobile
			count={orderPurchasedCoupon.quantity}
			disabled={orderPurchasedCoupon.quantity >= maxCartAdditionAmount}
			decrementLabel={translate(orderPurchasedCoupon.quantity > 1 ? "removeOne" : "removeFromCart")}
			incrementLabel={translate("addAnother")}
			{decrement}
			{increment}
		/>
	</div>
</div>
